import { FormEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

import { useLoginExternalApplication, useResendTwoFactor, useVerifyTwoFactor } from '@/services/auth.ts'
import { User } from '@/types/User'
import {
  findTwoFactorChallenge,
  hasPasswordReset,
  hasStrongMfaSetup,
  requirementsFrom,
  RichRequiredAction,
  SECOND_FACTOR_URN,
  SecondFactorKind,
} from '@/types'
import { extractConsentChallenge, useAutoGrantConsent } from '@/pages/external/hooks/useAutoGrantConsent'

import { Spinner } from '@/components/common'
import { Checkbox } from '../components'
import { AppShell, AppButton, AppLinkButton, AppTextField, CenteredCard } from '@/pages/external/components/shell'

import { TRACKING_TYPES } from '@/constants/TrackingConstants.ts'
import { APP_CUSTOM_ERROR, APP_TIMEOUT_ERROR } from '@/constants/MessageConstants.ts'
import { ROUTE_FORGOT_PASSWORD, ROUTE_TWO_FACTOR } from '@/constants/Routes.ts'
import { BASE_APP_URL } from '@/constants/Common.ts'
import { eventTracker } from '@/config/trackingFunctions.ts'

const RedeemSchema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
})

type RedeemForm = yup.InferType<typeof RedeemSchema>

interface RedeemLoginFormProps {
  onLoginSuccess: (token: string, user: User) => void
  clientKey: string
}

export function RedeemLoginForm({ onLoginSuccess, clientKey }: RedeemLoginFormProps) {
  const { t } = useTranslation()

  const [loginError, setLoginError] = useState<string | undefined>()
  const [enableTermsAndConditions, setEnableTermsAndConditions] = useState<boolean>(false)
  const [showLogin, setShowLogin] = useState<boolean>(true)

  // Set when the partner login answers with a second-factor challenge, which it always does now:
  // the card swaps from credentials to a code box. Dropped on Back so a retry starts a fresh one.
  const [challenge, setChallenge] = useState<{ token: string; methods: SecondFactorKind[] } | null>(null)
  const [challengeCode, setChallengeCode] = useState('')
  // Set when the server refuses the login because the account still owes first-party setup.
  const [setupRequired, setSetupRequired] = useState<{ passwordReset: boolean; strongMfa: boolean } | null>(null)

  const { mutate: externalLogin, isLoading } = useLoginExternalApplication()
  const { mutate: verifyTwoFactor, isLoading: isVerifying } = useVerifyTwoFactor()
  const { mutate: resendTwoFactor, isLoading: isResending } = useResendTwoFactor()
  const { autoGrantConsent, isGrantingConsent } = useAutoGrantConsent()

  const hasMethod = (urn: string) => challenge?.methods.some((m) => m.type === urn) ?? false
  const isTotpChallenge = hasMethod(SECOND_FACTOR_URN.TOTP)
  const hasCodeChallenge = isTotpChallenge || hasMethod(SECOND_FACTOR_URN.EMAIL) || hasMethod(SECOND_FACTOR_URN.SMS)

  const beginChallenge = (token: string, data: unknown) => {
    const methods = findTwoFactorChallenge((data as { requirements?: RichRequiredAction[] })?.requirements)
      ?.methods ?? [{ type: SECOND_FACTOR_URN.EMAIL }]
    setChallenge({ token, methods })
    setChallengeCode('')
    setLoginError(undefined)
  }

  const resetChallenge = () => {
    setChallenge(null)
    setChallengeCode('')
    setLoginError(undefined)
  }

  // The server refuses a partner login (428, nothing minted) for an account that still owes a
  // password reset or strong two-factor enrolment. Returns what is owed, or null for any other error.
  const extractSetupRequired = (error: unknown): { passwordReset: boolean; strongMfa: boolean } | null => {
    if ((error as { response?: { status?: number } })?.response?.status !== 428) return null
    const requirements = requirementsFrom(error)
    const passwordReset = hasPasswordReset(requirements)
    const strongMfa = hasStrongMfaSetup(requirements)
    return passwordReset || strongMfa ? { passwordReset, strongMfa } : null
  }

  // The partial states shared by the login and the code step: consent (granted on the member's
  // behalf, as before) and owed setup. Returns false when the error is neither.
  const handlePartialState = (error: unknown): boolean => {
    const consent = extractConsentChallenge(error)
    if (consent) {
      autoGrantConsent(
        consent,
        (accessToken, _refreshToken, user) => onLoginSuccess(accessToken, user),
        (msg) => setLoginError(msg)
      )
      return true
    }
    const owed = extractSetupRequired(error)
    if (owed) {
      resetChallenge()
      setSetupRequired(owed)
      return true
    }
    return false
  }

  const onSubmitChallenge = (event: FormEvent) => {
    event.preventDefault()
    if (!challenge || challengeCode.length < 6) return
    setLoginError(undefined)
    verifyTwoFactor(
      // Strong factor -> send the kind; a delivered (email/SMS) code -> omit it.
      {
        challengeToken: challenge.token,
        code: challengeCode,
        method: isTotpChallenge ? { type: SECOND_FACTOR_URN.TOTP } : undefined,
      },
      {
        // Consent off: the verify answer is the session itself.
        onSuccess: (data) => onLoginSuccess(data.result.accessToken, data.result.user),
        // Consent on: a correct code is answered with the consent 428.
        onError: (error) => {
          if (handlePartialState(error)) return
          setLoginError(
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              t('twoFactor.invalidCode', 'That code was not accepted. Try again.')
          )
        },
      }
    )
  }

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<RedeemForm>({
    resolver: yupResolver(RedeemSchema),
  })

  const onSubmit = handleSubmit(async (data) => {
    setLoginError(undefined)

    try {
      const payload = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        partnerId: clientKey,
      }

      externalLogin(payload, {
        onSuccess: (loginData) => {
          // An older server answered the challenge as a 200 with no session.
          if (loginData.result?.twoFactorRequired && loginData.result?.challengeToken) {
            beginChallenge(loginData.result.challengeToken, loginData.result)
            return
          }
          onLoginSuccess(loginData.result.accessToken, loginData.result.user)
        },
        onError: (error: unknown) => {
          // The partner login always asks for the second factor, as a 428 carrying the challenge.
          const challengeToken =
            (error as { response?: { status?: number } })?.response?.status === 428
              ? findTwoFactorChallenge(requirementsFrom(error))?.challengeToken
              : undefined
          if (challengeToken) {
            beginChallenge(challengeToken, (error as { response?: { data?: unknown } }).response?.data)
            return
          }
          if (handlePartialState(error)) return
          const errorMessage = getErrorMessage(error)
          setLoginError(errorMessage)
        },
      })
    } catch (error) {
      setLoginError(APP_CUSTOM_ERROR)
    }
  })

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      const apiError = error as { response?: { data?: { message?: string } }; code?: string }
      if (apiError.response?.data?.message) {
        return apiError.response.data.message
      }
      if (apiError.code === 'ECONNABORTED') {
        return APP_TIMEOUT_ERROR
      }
    }
    return APP_CUSTOM_ERROR
  }

  const renderLoginOptions = () => (
    <div className="flex flex-col items-stretch w-full gap-y-3">
      <AppButton fullWidth onClick={() => setShowLogin(true)}>
        {t('auth.signIn')}
      </AppButton>

      <a
        href={`${BASE_APP_URL}/signup`}
        target="_blank"
        rel="noopener noreferrer"
        className="border border-[color:var(--accent)] text-[color:var(--accent)] text-base font-semibold w-full h-14 inline-flex items-center justify-center rounded-[var(--radius-button)] hover:bg-[color:var(--accent-soft)] transition-colors"
      >
        {t('auth.signUp')}
      </a>

      <p className="text-[color:var(--ink-soft)] text-sm text-center mt-2">
        {t('auth.claimQuestion')}{' '}
        <a
          href={`${BASE_APP_URL}/claim`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-[color:var(--accent)] hover:opacity-70"
        >
          {t('auth.claimAccount')}
        </a>
      </p>
    </div>
  )

  const renderLoginForm = () => (
    <div className="flex flex-col gap-y-4">
      <a
        href={`${BASE_APP_URL}/signup`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[color:var(--ink-faint)] text-xs text-right hover:text-[color:var(--ink-soft)] transition-colors"
      >
        ← {t('auth.noAccount', "I don't have an account")}
      </a>

      {loginError && (
        <p
          className="text-sm text-[color:var(--negative)] bg-[color:var(--negative)]/8 border border-[color:var(--negative)]/20 rounded-[var(--radius-input)] px-3 py-2"
          role="alert"
        >
          {loginError}
        </p>
      )}

      <AppTextField
        label={t('common.email')}
        placeholder={t('common.emailPlaceHolder')}
        error={errors.email?.message}
        variant="email"
        autoComplete="email"
        {...register('email')}
      />

      <AppTextField
        label={t('common.password')}
        placeholder={t('common.passwordPlaceHolder')}
        error={errors.password?.message}
        variant="password"
        autoComplete="current-password"
        {...register('password')}
      />

      <Checkbox
        label={t('common.externalLoginCheckbox')}
        value={enableTermsAndConditions}
        labelClassName="text-xs text-[color:var(--ink-soft)]"
        setValue={setEnableTermsAndConditions}
      />

      <div className="flex flex-col items-stretch w-full gap-y-2 mt-2">
        <AppButton
          type="submit"
          fullWidth
          disabled={!enableTermsAndConditions || isLoading || isGrantingConsent}
          loading={isLoading}
          onClick={() => {
            eventTracker(TRACKING_TYPES.CONNECTED_ACCOUNT, {
              partner_id: clientKey,
            })
          }}
        >
          <span className="inline-flex items-center gap-x-2">
            <span>{isLoading ? t('common.logging') : t('common.login')}</span>
            {isLoading && <Spinner className="w-4 h-4 fill-white" />}
          </span>
        </AppButton>

        <Link
          to={ROUTE_FORGOT_PASSWORD}
          className="text-center text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] text-sm font-medium py-2 transition-colors"
        >
          {t('common.forgotPassword')}?
        </Link>
      </div>
    </div>
  )

  const heading = (text: string) => (
    <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
      {text}
    </h1>
  )

  if (setupRequired) {
    const steps = [
      setupRequired.passwordReset && t('external.setup.resetPassword', 'Set a new password for your account'),
      setupRequired.strongMfa &&
        t('external.setup.strongMfa', 'Set up two-factor authentication with an authenticator app or a passkey'),
    ].filter((s): s is string => !!s)
    const remedyPath = setupRequired.passwordReset ? ROUTE_FORGOT_PASSWORD : ROUTE_TWO_FACTOR
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <div className="flex flex-col items-center gap-y-4 w-full text-center">
            {heading(t('external.setup.title', 'Finish setting up your DUPR account'))}
            <p className="text-sm text-[color:var(--ink-soft)]">
              {t(
                'external.setup.body',
                'Before you can sign in with DUPR here, your account needs some additional setup:'
              )}
            </p>
            <ul className="text-sm text-[color:var(--ink)] text-left list-disc pl-6 self-stretch">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
            <p className="text-sm text-[color:var(--ink-soft)]">
              {t(
                'external.setup.where',
                'Do this on dupr.com or in the DUPR app, then come back here and sign in again.'
              )}
            </p>
            <AppLinkButton href={`${BASE_APP_URL}${remedyPath}`} target="_blank" rel="noopener noreferrer" fullWidth>
              {t('external.setup.openDupr', 'Open DUPR')}
            </AppLinkButton>
            <button
              type="button"
              onClick={() => setSetupRequired(null)}
              className="text-[color:var(--ink-faint)] text-xs hover:text-[color:var(--ink-soft)] transition-colors"
            >
              {t('common.back', 'Back')}
            </button>
          </div>
        </CenteredCard>
      </AppShell>
    )
  }

  // Second-factor code entry, shown in place of the credentials once the login answered with a
  // challenge. Passkeys are not performed on this partner surface (it can be framed), so a
  // passkey-only account is pointed at setting up an authenticator instead.
  if (challenge) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <form onSubmit={onSubmitChallenge} className="flex flex-col w-full gap-y-5">
            <div className="text-center">
              {heading(
                !hasCodeChallenge
                  ? t('twoFactor.passkeyUnavailableTitle', 'Passkeys are not available here yet')
                  : isTotpChallenge
                    ? t('twoFactor.authTitle', 'Enter your authenticator code')
                    : t('twoFactor.title', 'Check your email')
              )}
              <p className="text-sm text-[color:var(--ink-soft)] mt-2">
                {!hasCodeChallenge
                  ? t(
                      'twoFactor.passkeyUnavailableBody',
                      'Passkeys are not currently available in the partner sign-in flow. For now, please set up an authenticator app on your DUPR account and use its code to sign in here.'
                    )
                  : isTotpChallenge
                    ? t('twoFactor.authSubtitle', 'Enter the 6-digit code from your authenticator app.')
                    : t('twoFactor.subtitle', 'Enter the 6-digit code we just sent you.')}
              </p>
            </div>

            {hasCodeChallenge && (
              <input
                value={challengeCode}
                onChange={(e) => setChallengeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                aria-label={
                  isTotpChallenge
                    ? t('twoFactor.authCodeLabel', 'Authenticator code')
                    : t('twoFactor.codeLabel', 'Verification code')
                }
                className="w-full text-center tracking-[0.5em] text-2xl py-3 rounded-lg border border-[color:var(--line)] bg-transparent text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
              />
            )}

            {loginError && (
              <p className="text-sm text-center text-[color:var(--negative)]" role="alert">
                {loginError}
              </p>
            )}

            <div className="flex flex-col items-stretch w-full gap-y-2">
              {hasCodeChallenge ? (
                <AppButton
                  type="submit"
                  fullWidth
                  disabled={challengeCode.length < 6 || isVerifying || isGrantingConsent}
                  loading={isVerifying || isGrantingConsent}
                >
                  <span className="inline-flex items-center gap-x-2">
                    <span>{t('common.continue', 'Continue')}</span>
                    {(isVerifying || isGrantingConsent) && <Spinner className="w-4 h-4 fill-white" />}
                  </span>
                </AppButton>
              ) : (
                <AppLinkButton
                  href={`${BASE_APP_URL}${ROUTE_TWO_FACTOR}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                >
                  {t('twoFactor.setUpAuthenticator', 'Set up an authenticator app')}
                </AppLinkButton>
              )}
              {/* A resend only makes sense for a delivered code; the server refuses it once a strong factor is enrolled. */}
              {hasCodeChallenge && !isTotpChallenge && (
                <button
                  type="button"
                  disabled={isResending}
                  onClick={() => {
                    setLoginError(undefined)
                    resendTwoFactor(
                      { challengeToken: challenge.token },
                      {
                        onError: (error) =>
                          setLoginError(
                            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                              t('twoFactor.resendFailed', 'Could not resend the code.')
                          ),
                      }
                    )
                  }}
                  className="text-center text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] text-sm font-medium py-2 transition-colors"
                >
                  {t('twoFactor.resend', 'Send a new code')}
                </button>
              )}
              <button
                type="button"
                onClick={resetChallenge}
                className="text-center text-[color:var(--ink-faint)] hover:text-[color:var(--ink)] text-sm py-1 transition-colors"
              >
                {t('common.back', 'Back')}
              </button>
            </div>
          </form>
        </CenteredCard>
      </AppShell>
    )
  }

  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <form onSubmit={onSubmit} className="flex flex-col w-full gap-y-5">
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)]">
              Connect with DUPR
            </h1>
          </div>
          {showLogin ? renderLoginForm() : renderLoginOptions()}
        </form>
      </CenteredCard>
    </AppShell>
  )
}

export default RedeemLoginForm
